import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunityPage = () => {
  const [communityMeals, setCommunityMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 15,
    skip: 0
  });

  useEffect(() => {
    fetchCommunityMeals();
  }, [pagination.skip]);

  const fetchCommunityMeals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/custom-meals/community`, {
        params: {
          limit: pagination.limit,
          skip: pagination.skip
        }
      });

      // Handle both new and old response formats
      if (response.data && response.data.meals) {
        setCommunityMeals(response.data.meals);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || response.data.meals.length
        }));
      } else {
        // Fallback for old response format
        setCommunityMeals(Array.isArray(response.data) ? response.data : []);
        setPagination(prev => ({
          ...prev,
          total: Array.isArray(response.data) ? response.data.length : 0
        }));
      }
    } catch (error) {
      console.error('Error fetching community meals:', error);
      setCommunityMeals([]);
    } finally {
      setLoading(false);
    }
  };

  const upvoteMeal = async (mealId) => {
    try {
      await axios.post(`${API}/custom-meals/${mealId}/upvote`);
      fetchCommunityMeals();
    } catch (error) {
      console.error('Failed to upvote meal:', error);
      alert('Failed to upvote meal. Please try again.');
    }
  };

  const handlePageChange = (newPage) => {
    const newSkip = (newPage - 1) * pagination.limit;
    setPagination(prev => ({
      ...prev,
      skip: newSkip
    }));
    window.scrollTo(0, 0);
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit) || 1;
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key="first"
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 rounded border hover:bg-gray-100"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis-start" className="px-2">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded border ${
            currentPage === i ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis-end" className="px-2">...</span>);
      }
      pages.push(
        <button
          key="last"
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 rounded border hover:bg-gray-100"
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="flex justify-center mt-8">
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded border disabled:opacity-50 hover:bg-gray-100"
            aria-label="First page"
          >
            «
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded border disabled:opacity-50 hover:bg-gray-100"
            aria-label="Previous page"
          >
            ‹
          </button>

          {pages}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded border disabled:opacity-50 hover:bg-gray-100"
            aria-label="Next page"
          >
            ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded border disabled:opacity-50 hover:bg-gray-100"
            aria-label="Last page"
          >
            »
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="mb-6">
            <h4 className="heading-4">🌐 Community Meals</h4>
            <p className="text-gray-600">Discover and share meals created by our community</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
              <p className="text-gray-600">Loading community meals...</p>
            </div>
          ) : communityMeals.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600 mb-4">No community meals yet</p>
              <p className="text-gray-500">Be the first to share a meal with the community!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {communityMeals.map((meal) => (
                  <div key={meal.id} className="p-4 rounded-lg border hover:shadow-md transition-shadow" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-gray-900">{meal.name}</h3>
                      <button
                        onClick={() => upvoteMeal(meal.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
                        aria-label={`Upvote ${meal.name}`}
                      >
                        <span>👍</span>
                        <span className="text-sm font-semibold">{meal.upvotes || 0}</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                      <div><span className="text-gray-600">Calories:</span> <strong>{meal.calories}</strong></div>
                      <div><span className="text-gray-600">Protein:</span> <strong>{meal.protein}g</strong></div>
                      <div><span className="text-gray-600">Carbs:</span> <strong>{meal.carbs}g</strong></div>
                      <div><span className="text-gray-600">Fat:</span> <strong>{meal.fat}g</strong></div>
                    </div>

                    <p className="text-xs mb-2 text-gray-500">
                      {meal.type} • {meal.region} • {meal.category}
                    </p>

                    {meal.ingredients && meal.ingredients.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-gray-900">Ingredients:</p>
                        <p className="text-xs text-gray-600">
                          {meal.ingredients.slice(0, 3).join(', ')}
                          {meal.ingredients.length > 3 && '...'}
                        </p>
                      </div>
                    )}

                    <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between items-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMeal(meal);
                        }}
                        className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors"
                      >
                        <span>📝</span>
                        <span>View Recipe</span>
                      </button>

                      {meal.video_id && (
                        <a
                          href={`https://youtube.com/watch?v=${meal.video_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-red-50 hover:bg-red-100 text-red-700 font-medium px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>▶️</span>
                          <span>Watch Video</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Recipe Modal */}
      {selectedMeal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMeal(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold">{selectedMeal.name}</h2>
                <button
                  onClick={() => setSelectedMeal(null)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">Nutrition Information</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-600">Calories:</span> <strong>{selectedMeal.calories}</strong></div>
                    <div><span className="text-gray-600">Protein:</span> <strong>{selectedMeal.protein}g</strong></div>
                    <div><span className="text-gray-600">Carbs:</span> <strong>{selectedMeal.carbs}g</strong></div>
                    <div><span className="text-gray-600">Fat:</span> <strong>{selectedMeal.fat}g</strong></div>
                    {selectedMeal.servings && (
                      <div className="col-span-2"><span className="text-gray-600">Servings:</span> <strong>{selectedMeal.servings}</strong></div>
                    )}
                  </div>
                </div>
                
                {selectedMeal.video_id && (
                  <div className="md:col-span-2">
                    <h3 className="font-semibold text-lg mb-2">Source</h3>
                    <a 
                      href={`https://youtube.com/watch?v=${selectedMeal.video_id}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <span>▶️ Watch on YouTube</span>
                    </a>
                  </div>
                )}
              </div>

              {selectedMeal.ingredients && selectedMeal.ingredients.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">Ingredients</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {selectedMeal.ingredients.map((ingredient, index) => (
                      <li key={index} className="text-gray-700">{ingredient}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(selectedMeal.recipe || selectedMeal.instructions) && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Instructions</h3>
                  <div className="whitespace-pre-line text-gray-700">
                    {selectedMeal.recipe || selectedMeal.instructions}
                  </div>
                </div>
              )}

              {selectedMeal.notes && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-1">Notes</h4>
                  <p className="text-yellow-700 text-sm">{selectedMeal.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;